import {
	CheckOutlined,
	DownOutlined,
	ExclamationCircleOutlined,
	InfoCircleOutlined,
	LockOutlined,
	RightOutlined,
	SearchOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { Checkbox, Empty, Input, Segmented, Skeleton, Tag } from "antd";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermissionLabels } from "../hooks/usePermissionLabels";
import { useResources } from "../hooks/useRoles";
import type { Permission, ResourceDescriptor, RoleScope } from "../types";
import {
	PERMISSION_GROUPS,
	permissionKey,
	SENSITIVE_PERMISSIONS,
	sensitiveNoteKey,
} from "../utils/roleForm";
import { ChangeDot } from "./roleVisuals";

interface PermissionMatrixProps {
	/** Antd `Form.Item` bu proplarni o'zi uzatadi. */
	value?: Permission[];
	onChange?: (value: Permission[]) => void;
	/** Tahrirlashda saqlangan holat — o'zgarish belgilari shunga nisbatan. */
	baseline?: Permission[];
	/** `department` bo'lsa «Rollar» qatorida ogohlantirish chiqadi. */
	scope?: RoleScope;
	disabled?: boolean;
}

interface MatrixGroup {
	titleKey: string;
	rows: ResourceDescriptor[];
}

type Filter = "all" | "selected";

function toPermission(key: string): Permission {
	const separator = key.indexOf(":");
	return { resource: key.slice(0, separator), action: key.slice(separator + 1) };
}

function rowKeys(row: ResourceDescriptor): string[] {
	return row.actions.map((action) => `${row.name}:${action}`);
}

/**
 * Guruhlarga taqsimlash — ro'yxatdagi tartib saqlanadi. Hech bir guruhga
 * tushmagan resurs (kelajakda qo'shilishi mumkin) «Boshqa»ga tushadi, ya'ni
 * jimgina ko'rinmas bo'lib qolmaydi.
 */
function groupResources(resources: ResourceDescriptor[]): MatrixGroup[] {
	const grouped = PERMISSION_GROUPS.map((group) => ({
		titleKey: group.titleKey,
		rows: resources.filter((resource) => group.resources.includes(resource.name)),
	})).filter((group) => group.rows.length > 0);

	const groupedNames = new Set(PERMISSION_GROUPS.flatMap((group) => group.resources));
	const rest = resources.filter((resource) => !groupedNames.has(resource.name));
	if (rest.length > 0) {
		grouped.push({ titleKey: "groups.other", rows: rest });
	}

	return grouped;
}

function triState(keys: string[], selected: ReadonlySet<string>) {
	const count = keys.filter((key) => selected.has(key)).length;
	return {
		count,
		checked: count > 0 && count === keys.length,
		indeterminate: count > 0 && count < keys.length,
	};
}

const CHIP_BASE =
	"m-0 inline-flex h-[26px] cursor-pointer items-center gap-[5px] whitespace-nowrap rounded-md border px-2.5 font-[inherit] text-xs leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-60";
const CHIP_ON =
	"border-[#b9cde8] bg-[#eaf1fb] text-[#1c5cab] dark:border-[#2f4f7a] dark:bg-[#1c2b40] dark:text-[#8fb6ea]";
const CHIP_OFF =
	"border-[#e5e6e3] bg-white text-[#565e6b] hover:border-[#1c5cab] hover:text-[#1c5cab] dark:border-[#2a2e33] dark:bg-[#181b1f] dark:text-[#a3aab5] dark:hover:border-[#6fa3e6] dark:hover:text-[#8fb6ea]";
const CHIP_REMOVED =
	"border-dashed border-[#d9b35c] bg-white text-[#8a929e] dark:border-[#8a6d2a] dark:bg-[#181b1f] dark:text-[#6b7280]";

interface PermissionChipProps {
	action: string;
	label: string;
	on: boolean;
	changed: boolean;
	sensitive: boolean;
	disabled: boolean;
	onToggle: () => void;
}

/**
 * Bitta `resource:action` — bosiladigan chip. Texnik amal ID si `title` da
 * (hover), ko'rinadigan matn tarjima.
 */
function PermissionChip({
	action,
	label,
	on,
	changed,
	sensitive,
	disabled,
	onToggle,
}: PermissionChipProps) {
	const removed = changed && !on;
	const tone = on ? CHIP_ON : removed ? CHIP_REMOVED : CHIP_OFF;

	return (
		<button
			type="button"
			aria-pressed={on}
			disabled={disabled}
			onClick={onToggle}
			className={`${CHIP_BASE} ${tone}`}
		>
			{on ? <CheckOutlined className="text-[10px]" /> : null}
			<span title={action} className={removed ? "line-through" : undefined}>
				{label}
			</span>
			{sensitive ? <ExclamationCircleOutlined className="text-[#b7791f]" /> : null}
			{changed ? <ChangeDot /> : null}
		</button>
	);
}

function RowNote({ icon, children }: { icon: ReactNode; children: ReactNode }) {
	return (
		<div className="flex items-start gap-1.5 text-xs leading-normal text-[#8a5a00] dark:text-[#e3b75a]">
			<span className="mt-px text-[#b7791f]">{icon}</span>
			<span>{children}</span>
		</div>
	);
}

function Legend({ showChanged }: { showChanged: boolean }) {
	const { t } = useTranslation("roles");

	return (
		<div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs leading-[18px] text-[#8a929e] dark:text-[#6b7280]">
			{showChanged ? (
				<span className="inline-flex items-center gap-1.5">
					<ChangeDot />
					{t("picker.legendChanged")}
				</span>
			) : null}
			<span className="inline-flex items-center gap-1.5">
				<ExclamationCircleOutlined className="text-[#b7791f]" />
				{t("picker.legendSensitive")}
			</span>
			<span className="inline-flex items-center gap-1.5">
				<InfoCircleOutlined />
				{t("picker.legendScoped")}
			</span>
		</div>
	);
}

/**
 * Resurs × amal katalogi. Backend `/roles/permissions` har resursga FAQAT
 * o'z amallarini qaytaradi (`resourceActions`, ~74 juftlik) — shuning uchun
 * yangi resurs yoki amal qo'shilganda UI o'zi yangilanadi.
 *
 * ⚠️⚠️ Katalogda YO'Q juftliklar (eski 338 katakli forma orqali yaratilgan
 * rollar) JIMGINA TASHLANMAYDI: qator/guruh belgilash faqat o'z kalitlariga
 * tegadi va ular alohida «Ro'yxatdan tashqari ruxsatlar» blokida ko'rinadi —
 * saqlashda o'zgarmaydi, faqat foydalanuvchi o'zi olib tashlasa o'chadi.
 */
export function PermissionMatrix(props: PermissionMatrixProps) {
	const { t } = useTranslation("roles");
	const { data: resources = [], isLoading } = useResources();

	// ⚠️ Ichki qism FAQAT ma'lumot tayyor bo'lgach mount qilinadi: ochiq
	// guruhlar ro'yxati birinchi render'da `value` + `resources` dan
	// hisoblanadi (`useState` initsializatori) — sovuq keshda `resources=[]`
	// bilan mount bo'lsa, ma'lumot kelgach barcha guruhlar yopiq qolardi.
	if (isLoading) {
		return <Skeleton active />;
	}

	if (resources.length === 0) {
		return <Empty description={t("picker.emptyResources")} />;
	}

	return <MatrixBody {...props} resources={resources} />;
}

function MatrixBody({
	value = [],
	onChange,
	baseline,
	scope,
	disabled = false,
	resources,
}: PermissionMatrixProps & { resources: ResourceDescriptor[] }) {
	const { t } = useTranslation("roles");
	const { resourceLabel, actionLabel } = usePermissionLabels();

	const groups = groupResources(resources);
	const selected = new Set(value.map(permissionKey));
	const before = baseline ? new Set(baseline.map(permissionKey)) : null;
	const known = new Set(resources.flatMap(rowKeys));

	// Birinchi ochilishda — tanlangan ruxsati bor guruhlar ochiq, qolgani
	// yig'ilgan (yangi rolda hammasi yig'ilgan: 8 qatorli qisqa ro'yxat).
	const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(
		() =>
			new Set(
				groups
					.filter((group) =>
						group.rows.some((row) => rowKeys(row).some((key) => selected.has(key)))
					)
					.map((group) => group.titleKey)
			)
	);
	const [filter, setFilter] = useState<Filter>("all");
	const [query, setQuery] = useState("");

	const isChanged = (key: string) => before !== null && before.has(key) !== selected.has(key);

	/** ⚠️ Faqat `keys` ga tegadi — boshqa (jumladan katalogdan tashqari) juftliklar saqlanadi. */
	const setKeys = (keys: string[], checked: boolean) => {
		const affected = new Set(keys);
		const kept = value.filter((permission) => !affected.has(permissionKey(permission)));
		onChange?.(checked ? [...kept, ...keys.map(toPermission)] : kept);
	};

	const toggleGroup = (titleKey: string) => {
		setOpenGroups((previous) => {
			const next = new Set(previous);
			if (next.has(titleKey)) {
				next.delete(titleKey);
			} else {
				next.add(titleKey);
			}
			return next;
		});
	};

	const needle = query.trim().toLocaleLowerCase();
	const filtering = filter === "selected" || needle !== "";

	const keyVisible = (key: string, texts: string[]) => {
		if (filter === "selected" && !(selected.has(key) || isChanged(key))) {
			return false;
		}
		return needle === "" || texts.some((text) => text.toLocaleLowerCase().includes(needle));
	};

	const rowVisible = (row: ResourceDescriptor) => {
		const texts = [row.name, resourceLabel(row.name), ...row.actions.map(actionLabel)];
		return rowKeys(row).some((key) => keyVisible(key, texts));
	};

	const visibleGroups = groups
		.map((group) => ({ ...group, rows: filtering ? group.rows.filter(rowVisible) : group.rows }))
		.filter((group) => group.rows.length > 0);

	const legacyKeys = [...new Set([...value, ...(baseline ?? [])].map(permissionKey))]
		.filter((key) => !(known.has(key) || key.includes("*")))
		.filter((key) => {
			const { resource, action } = toPermission(key);
			return keyVisible(key, [key, resourceLabel(resource), actionLabel(action)]);
		});

	const selectedCount = [...selected].filter((key) => known.has(key)).length;

	const renderChip = (key: string) => {
		const { resource, action } = toPermission(key);
		const on = selected.has(key);

		return (
			<PermissionChip
				key={key}
				action={action}
				label={
					known.has(key)
						? actionLabel(action)
						: `${resourceLabel(resource)}: ${actionLabel(action)}`
				}
				on={on}
				changed={isChanged(key)}
				sensitive={SENSITIVE_PERMISSIONS.has(key)}
				disabled={disabled}
				onToggle={() => setKeys([key], !on)}
			/>
		);
	};

	const renderRow = (row: ResourceDescriptor) => {
		const keys = rowKeys(row);
		const state = triState(keys, selected);

		const notes: ReactNode[] = keys
			.filter((key) => SENSITIVE_PERMISSIONS.has(key) && selected.has(key))
			.map((key) => (
				<RowNote key={key} icon={<ExclamationCircleOutlined />}>
					<span className="font-medium">{actionLabel(toPermission(key).action)}</span> —{" "}
					{t(sensitiveNoteKey(key))}
				</RowNote>
			));

		// CLAUDE.md «Kirish ko'lami» 4-qoidasi: dvigatel tayinlash cheklovini
		// o'qimaydi — bo'lim doirasidagi rol egasi o'ziga cheklovsiz rol berib,
		// doirasidan chiqib keta oladi.
		if (row.name === "roles" && scope === "department") {
			notes.push(
				<RowNote key="department-warning" icon={<WarningOutlined />}>
					{t("picker.departmentRolesWarning")}
				</RowNote>
			);
		}

		return (
			<div
				key={row.name}
				data-resource={row.name}
				className="grid grid-cols-1 gap-x-3 gap-y-2 border-t border-[#eeefec] px-3 py-[9px] sm:grid-cols-[200px_minmax(0,1fr)] dark:border-[#22262b]"
			>
				<div className="flex min-h-[26px] items-center">
					<Checkbox
						checked={state.checked}
						indeterminate={state.indeterminate}
						disabled={disabled}
						onChange={(event) => setKeys(keys, event.target.checked)}
					>
						{/* Texnik ID — hover'da (`title`), ko'rinadigan matn tarjima. */}
						<span className="font-medium" title={row.name}>
							{resourceLabel(row.name)}
						</span>
					</Checkbox>
				</div>
				<div className="flex flex-col gap-2">
					<div className="flex flex-wrap gap-1.5">{keys.map(renderChip)}</div>
					{notes}
				</div>
			</div>
		);
	};

	return (
		<div>
			<div className="mb-2 flex flex-wrap items-center gap-3">
				<div className="flex items-baseline gap-2">
					<span className="text-sm font-semibold">{t("picker.title")}</span>
					<span className="text-[#8a929e] dark:text-[#6b7280]">
						{t("picker.selectedCount", { selected: selectedCount, total: known.size })}
					</span>
				</div>
				<div className="ml-auto flex flex-wrap items-center gap-2">
					<Segmented<Filter>
						value={filter}
						onChange={setFilter}
						options={[
							{ label: t("picker.filterAll"), value: "all" },
							{ label: t("picker.filterSelected"), value: "selected" },
						]}
					/>
					<Input
						allowClear
						prefix={<SearchOutlined className="text-[#8a929e]" />}
						placeholder={t("picker.searchPlaceholder")}
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						style={{ width: 200 }}
					/>
				</div>
			</div>

			<Legend showChanged={baseline !== undefined} />

			{visibleGroups.length === 0 && legacyKeys.length === 0 ? (
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("picker.noMatches")} />
			) : (
				<div className="overflow-hidden rounded-lg border border-[#e5e6e3] dark:border-[#2a2e33]">
					{visibleGroups.map((group, index) => {
						const groupKeys = group.rows.flatMap(rowKeys);
						const state = triState(groupKeys, selected);
						const open = filtering || openGroups.has(group.titleKey);
						const changed = groupKeys.some(isChanged);

						return (
							<div
								key={group.titleKey}
								className={
									index === 0 ? undefined : "border-t border-[#e5e6e3] dark:border-[#2a2e33]"
								}
							>
								<div className="flex items-center gap-2.5 bg-[#fafaf9] px-3 py-2 dark:bg-[#1d2126]">
									<button
										type="button"
										aria-expanded={open}
										aria-label={t("picker.toggleGroup")}
										disabled={filtering}
										onClick={() => toggleGroup(group.titleKey)}
										className="m-0 flex h-5 w-5 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[#565e6b] disabled:cursor-default dark:text-[#a3aab5]"
									>
										{open ? (
											<DownOutlined className="text-[11px]" />
										) : (
											<RightOutlined className="text-[11px]" />
										)}
									</button>
									<Checkbox
										checked={state.checked}
										indeterminate={state.indeterminate}
										disabled={disabled}
										onChange={(event) => setKeys(groupKeys, event.target.checked)}
									/>
									<button
										type="button"
										tabIndex={-1}
										disabled={filtering}
										onClick={() => toggleGroup(group.titleKey)}
										className="m-0 cursor-pointer border-0 bg-transparent p-0 text-left font-[inherit] font-semibold text-inherit disabled:cursor-default"
									>
										{t(group.titleKey)}
									</button>
									<Tag color={state.count > 0 ? "blue" : "default"} className="!m-0">
										{`${state.count}/${groupKeys.length}`}
									</Tag>
									{changed ? <ChangeDot /> : null}
								</div>
								{open ? group.rows.map(renderRow) : null}
							</div>
						);
					})}

					{legacyKeys.length > 0 ? (
						<div
							className={`px-3 py-2.5 ${visibleGroups.length > 0 ? "border-t border-[#e5e6e3] dark:border-[#2a2e33]" : ""}`}
						>
							<div className="font-semibold">{t("picker.legacyTitle")}</div>
							<div className="mb-2 text-xs leading-normal text-[#8a929e] dark:text-[#6b7280]">
								{t("picker.legacyHint")}
							</div>
							<div className="flex flex-wrap gap-1.5">{legacyKeys.map(renderChip)}</div>
						</div>
					) : null}
				</div>
			)}
		</div>
	);
}

/** `*` rol (seed'dagi `admin`) — matritsa `*` ni ko'rsata olmaydi, ruxsatlar qulflangan. */
export function LockedPermissions() {
	const { t } = useTranslation("roles");

	return (
		<div>
			<div className="mb-3 flex items-baseline gap-2">
				<span className="text-sm font-semibold">{t("picker.title")}</span>
				<span className="text-[#8a929e] dark:text-[#6b7280]">{t("picker.lockedLabel")}</span>
			</div>
			<div className="overflow-hidden rounded-lg border border-[#e5e6e3] dark:border-[#2a2e33]">
				<div className="flex items-center gap-2.5 border-b border-[#eeefec] bg-[#fafaf9] px-3.5 py-3 dark:border-[#22262b] dark:bg-[#1d2126]">
					<LockOutlined className="text-[#565e6b] dark:text-[#a3aab5]" />
					<div>
						<div className="font-semibold">{t("picker.lockedTitle")}</div>
						<div className="text-xs leading-normal text-[#8a929e] dark:text-[#6b7280]">
							{t("picker.lockedHint")}
						</div>
					</div>
				</div>
				<div className="grid grid-cols-1 sm:grid-cols-2">
					{PERMISSION_GROUPS.map((group) => (
						<div
							key={group.titleKey}
							className="-mt-px flex items-center gap-2 border-t border-[#eeefec] px-3.5 py-[9px] sm:odd:border-r dark:border-[#22262b]"
						>
							<CheckOutlined className="text-[#8a929e]" />
							<span className="flex-1">{t(group.titleKey)}</span>
							<span className="text-xs text-[#8a929e] dark:text-[#6b7280]">
								{t("picker.lockedGroup")}
							</span>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
